export type Language = 'en' | 'fi';

export const defaultLanguage: Language = 'en';

export const copy = {
  en: {
    'meta.title': 'Guest registration | Check-in details',
    'meta.description':
      'Confirm your reservation, submit the required guest details, and receive your check-in details securely.',
    'hero.eyebrow': 'Apartment arrival flow',
    'hero.title': 'Confirm your stay and receive your check-in details',
    'hero.copy':
      'Use this secure form to verify the reservation, submit the legally required guest details, and unlock the arrival instructions for the apartment.',
    'summary.kicker': 'Before you arrive',
    'summary.step1': 'Match the booking against the reservation dates and last name.',
    'summary.step2': 'Submit the guest details required for accommodation registration.',
    'summary.step3': 'Read and save the check-in details shown after registration is complete.',
    'summary.refreshTitle': 'If the booking is not found immediately',
    'summary.refreshBody':
      'Double-check the channel, dates, and last name exactly as shown in your confirmation, then try again.',
    'privacy.title': 'Privacy and guest data',
    'privacy.body1':
      'The details requested on this page are collected to meet accommodation registration obligations and to manage your stay at the apartment.',
    'privacy.body2':
      'The information is used only by the accommodation host and parties directly involved in arranging your stay. It is not used for marketing.',
    'privacy.body3':
      'Registration records are stored securely and kept only for the period required by law or practical stay management needs.',
    'claim.kicker': 'Step 1',
    'claim.title': 'Find your reservation',
    'claim.intro':
      'Enter the stay dates and the last name used for the reservation.',
    'claim.channelAuto.airbnb': 'This link is for Airbnb reservations.',
    'claim.channelAuto.booking': 'This link is for Booking.com reservations.',
    'claim.airbnb': 'Airbnb',
    'claim.booking': 'Booking.com',
    'claim.checkIn': 'Check-in date',
    'claim.checkInHelp': 'Use the arrival date from the reservation confirmation.',
    'claim.checkOut': 'Check-out date',
    'claim.checkOutHelp': 'Use the departure date from the reservation confirmation.',
    'claim.lastName': 'Last name on the booking',
    'claim.lastNameHelp':
      'Enter the surname exactly as shown in the booking confirmation.',
    'claim.submit.idle': 'Find my reservation',
    'claim.submit.loading': 'Checking reservation...',
    'register.kicker': 'Step 2',
    'register.title': 'Submit guest details',
    'register.intro':
      'Complete the required details for every guest in the reservation. Select the guest count first, then fill the visible guest sections.',
    'register.guestCardTitle': 'Guest {index}',
    'register.firstNames': 'First names',
    'register.firstNamesHelp': 'Enter all given names exactly as shown in the official document.',
    'register.lastName': 'Last name',
    'register.lastNameHelp': 'Family name exactly as shown in the official document.',
    'register.socialIdNumber': 'Social ID number',
    'register.socialIdNumberHelp': 'Finnish personal identity code or the guest’s equivalent social ID number.',
    'register.nationality': 'Nationality',
    'register.nationalityHelp': 'State the nationality shown on the official document. Use Finland or Suomi for Finnish guests.',
    'register.address': 'Address',
    'register.addressHelp': 'Permanent home address of this guest.',
    'register.documentNumber': 'Passport or ID card number',
    'register.documentNumberHelp': 'Required only if the guest nationality is not Finland.',
    'register.countryOfOrigin': 'Country of origin',
    'register.countryOfOriginHelp': 'Required only if the guest nationality is not Finland.',
    'register.numberOfGuests': 'Number of guests',
    'register.numberOfGuestsHelp': 'Select 1 to 4 guests. The matching number of guest detail sections will be shown.',
    'register.email': 'Email address',
    'register.emailHelp': 'Reservation-level contact email for this stay.',
    'register.phone': 'Phone number',
    'register.phoneHelp': 'Reservation-level contact phone number for this stay.',
    'register.confirmation':
      'I confirm that the submitted guest details are complete and accurate.',
    'register.submit.idle': 'Submit guest details',
    'register.submit.loading': 'Submitting guest details...',
    'complete.kicker': 'Step 3',
    'complete.title': 'Your check-in details',
    'complete.copy':
      'Guest registration is complete. Keep the details below within your travelling party.',
    'complete.reset': 'Start another registration',
    'reservation.summary': 'Reservation: {channel} | {checkIn} to {checkOut}',
    'reservation.note.lastNameVerified': 'Last name matched the reservation details.',
    'reservation.note.dateRangeOnly':
      'The reservation was confirmed using the stay dates.',
    'banner.sessionRestored': 'Check-in details were restored from your saved session.',
    'banner.registrationComplete': 'Guest registration completed successfully.',
    'banner.claimRequired': 'Find the reservation before submitting guest details.',
    'banner.confirmationRequired': 'Please confirm that the submitted details are correct.',
    'banner.claimLoading': 'Checking the reservation. This may take a moment.',
    'banner.registrationLoading': 'Submitting the guest details securely.',
    'errors.genericRequest': 'The request could not be completed. Please try again.',
    'errors.genericSubmission': 'The guest details could not be submitted. Please try again.',
    'errors.routeNotFound': 'The requested page was not found.',
    'errors.requestBodyRequired': 'The request was empty. Please try again.',
    'errors.invalidJson': 'The request format was invalid. Please reload the page and try again.',
    'errors.missingQueryParameter': 'A required request value was missing.',
    'errors.validationFailed': 'Some required details are missing or invalid.',
    'errors.reservationNotFound':
      'No claimable reservation was found for those dates. Check the channel, dates, and last name, then try again.',
    'errors.registrationCompleted':
      'Guest registration has already been completed for this reservation.',
    'errors.lastNameMismatch': 'The last name did not match the reservation details.',
    'errors.reservationLocked':
      'This reservation is already being completed in another active session. Please wait and try again.',
    'errors.claimTokenInvalid': 'Your session is no longer valid. Find the reservation again to continue.',
    'errors.claimTokenExpired': 'Your session expired. Find the reservation again to continue.',
    'errors.registrationNotCompleted': 'Guest registration is not complete yet.',
    'errors.invalidDateRange': 'The departure date must be after the arrival date.',
    'errors.temporaryServiceError': 'The service is temporarily unavailable. Please try again shortly.',
    'errors.claimMissingFields': 'Enter the booking channel, stay dates, and last name.',
    'errors.registrationMissingFields': 'Complete all required guest details before submitting.',
    'errors.invalidEmail': 'Enter a valid email address.',
    'errors.invalidFinnishSocialId': 'Enter a valid Finnish personal identity code.',
    'errors.invalidGuestCount': 'Select a guest count between 1 and 4 and complete that many guest sections.',
    'errors.unknownChannelHost':
      'Open the Airbnb or Booking.com check-in link provided to you and try again.',
  },
  fi: {
    'meta.title': 'Vierasrekisteröinti | Sisäänkirjautumisohjeet',
    'meta.description':
      'Vahvista varauksesi, täytä vaaditut vierastiedot ja vastaanota sisäänkirjautumisohjeet turvallisesti.',
    'hero.eyebrow': 'Huoneiston saapumisprosessi',
    'hero.title': 'Vahvista majoituksesi ja vastaanota sisäänkirjautumisohjeet',
    'hero.copy':
      'Tällä suojatulla lomakkeella vahvistat varauksen, täytät lain edellyttämät vierastiedot ja saat huoneiston saapumisohjeet.',
    'summary.kicker': 'Ennen saapumista',
    'summary.step1': 'Varaus tarkistetaan varauskanavan, päivämäärien ja sukunimen perusteella.',
    'summary.step2': 'Täytä majoitusrekisteriä varten vaaditut vierastiedot.',
    'summary.step3': 'Lue ja tallenna rekisteröinnin jälkeen näkyvät sisäänkirjautumisohjeet.',
    'summary.refreshTitle': 'Jos varausta ei löydy heti',
    'summary.refreshBody':
      'Tarkista varauskanava, päivämäärät ja sukunimi täsmälleen varausvahvistuksen mukaisesti ja yritä uudelleen.',
    'privacy.title': 'Tietosuoja ja vierastiedot',
    'privacy.body1':
      'Tällä sivulla pyydetyt tiedot kerätään majoitusrekisteröintiä koskevien velvoitteiden täyttämiseksi ja majoituksesi hoitamiseksi.',
    'privacy.body2':
      'Tietoja käyttävät vain majoittaja ja majoituksesi järjestämiseen suoraan osallistuvat tahot. Tietoja ei käytetä markkinointiin.',
    'privacy.body3':
      'Rekisteröintitiedot säilytetään turvallisesti vain lain tai majoituksen käytännön hoitamisen kannalta tarpeellisen ajan.',
    'claim.kicker': 'Vaihe 1',
    'claim.title': 'Etsi varauksesi',
    'claim.intro':
      'Anna majoituksen päivämäärät ja varauksella käytetty sukunimi.',
    'claim.channelAuto.airbnb': 'Tämä linkki on Airbnb-varauksille.',
    'claim.channelAuto.booking': 'Tämä linkki on Booking.com-varauksille.',
    'claim.airbnb': 'Airbnb',
    'claim.booking': 'Booking.com',
    'claim.checkIn': 'Saapumispäivä',
    'claim.checkInHelp': 'Käytä varausvahvistuksessa näkyvää saapumispäivää.',
    'claim.checkOut': 'Lähtöpäivä',
    'claim.checkOutHelp': 'Käytä varausvahvistuksessa näkyvää lähtöpäivää.',
    'claim.lastName': 'Varauksella oleva sukunimi',
    'claim.lastNameHelp':
      'Kirjoita sukunimi täsmälleen samassa muodossa kuin varausvahvistuksessa.',
    'claim.submit.idle': 'Etsi varaukseni',
    'claim.submit.loading': 'Haetaan varausta...',
    'register.kicker': 'Vaihe 2',
    'register.title': 'Täytä vierastiedot',
    'register.intro':
      'Täytä vaaditut tiedot jokaisesta varaukseen kuuluvasta vieraasta. Valitse ensin vieraiden määrä ja täytä sitten näkyvät vieraskortit.',
    'register.guestCardTitle': 'Vieras {index}',
    'register.firstNames': 'Etunimet',
    'register.firstNamesHelp': 'Kirjoita kaikki etunimet täsmälleen virallisessa asiakirjassa näkyvässä muodossa.',
    'register.lastName': 'Sukunimi',
    'register.lastNameHelp': 'Sukunimi täsmälleen virallisen asiakirjan mukaisesti.',
    'register.socialIdNumber': 'Henkilötunnus',
    'register.socialIdNumberHelp': 'Suomalainen henkilötunnus tai vastaava sosiaalinen tunnusnumero.',
    'register.nationality': 'Kansalaisuus',
    'register.nationalityHelp': 'Ilmoita virallisessa asiakirjassa näkyvä kansalaisuus. Käytä suomalaisille vieraille arvoa Finland tai Suomi.',
    'register.address': 'Osoite',
    'register.addressHelp': 'Tämän vieraan pysyvä kotiosoite.',
    'register.documentNumber': 'Passin tai henkilökortin numero',
    'register.documentNumberHelp': 'Pakollinen vain, jos vieraan kansalaisuus ei ole Suomi.',
    'register.countryOfOrigin': 'Lähtömaa',
    'register.countryOfOriginHelp': 'Pakollinen vain, jos vieraan kansalaisuus ei ole Suomi.',
    'register.numberOfGuests': 'Vieraiden määrä',
    'register.numberOfGuestsHelp': 'Valitse 1–4 vierasta. Lomake näyttää vastaavan määrän vierastietokortteja.',
    'register.email': 'Sähköpostiosoite',
    'register.emailHelp': 'Tämän majoituksen yhteinen yhteyssähköposti.',
    'register.phone': 'Puhelinnumero',
    'register.phoneHelp': 'Tämän majoituksen yhteinen yhteyspuhelinnumero.',
    'register.confirmation': 'Vahvistan, että lähetetyt vierastiedot ovat täydelliset ja oikein.',
    'register.submit.idle': 'Lähetä vierastiedot',
    'register.submit.loading': 'Lähetetään vierastietoja...',
    'complete.kicker': 'Vaihe 3',
    'complete.title': 'Sisäänkirjautumisohjeesi',
    'complete.copy':
      'Vierasrekisteröinti on valmis. Säilytä alla olevat tiedot vain matkaseurueesi käytössä.',
    'complete.reset': 'Aloita uusi rekisteröinti',
    'reservation.summary': 'Varaus: {channel} | {checkIn} - {checkOut}',
    'reservation.note.lastNameVerified': 'Sukunimi vastasi varaustietoja.',
    'reservation.note.dateRangeOnly':
      'Varaus vahvistettiin majoituksen päivämäärien perusteella.',
    'banner.sessionRestored': 'Sisäänkirjautumisohjeet palautettiin tallennetusta istunnosta.',
    'banner.registrationComplete': 'Vierasrekisteröinti lähetettiin onnistuneesti.',
    'banner.claimRequired': 'Etsi varaus ennen vierastietojen lähettämistä.',
    'banner.confirmationRequired': 'Vahvista ennen lähetystä, että annetut tiedot ovat oikein.',
    'banner.claimLoading': 'Varausta tarkistetaan. Tämä voi kestää hetken.',
    'banner.registrationLoading': 'Vierastietoja lähetetään turvallisesti.',
    'errors.genericRequest': 'Pyyntöä ei voitu suorittaa. Yritä uudelleen.',
    'errors.genericSubmission': 'Vierastietojen lähetys ei onnistunut. Yritä uudelleen.',
    'errors.routeNotFound': 'Pyydettyä sivua ei löytynyt.',
    'errors.requestBodyRequired': 'Pyyntö oli tyhjä. Yritä uudelleen.',
    'errors.invalidJson': 'Pyynnön muoto oli virheellinen. Päivitä sivu ja yritä uudelleen.',
    'errors.missingQueryParameter': 'Pyynnöstä puuttui pakollinen arvo.',
    'errors.validationFailed': 'Joitakin pakollisia tietoja puuttuu tai ne ovat virheellisiä.',
    'errors.reservationNotFound':
      'Näillä tiedoilla ei löytynyt vahvistettavaa varausta. Tarkista kanava, päivämäärät ja sukunimi ja yritä uudelleen.',
    'errors.registrationCompleted': 'Vierasrekisteröinti on jo tehty tälle varaukselle.',
    'errors.lastNameMismatch': 'Sukunimi ei vastannut varaustietoja.',
    'errors.reservationLocked':
      'Tätä varausta käsitellään jo toisessa aktiivisessa istunnossa. Odota hetki ja yritä uudelleen.',
    'errors.claimTokenInvalid': 'Istunto ei ole enää voimassa. Etsi varaus uudelleen jatkaaksesi.',
    'errors.claimTokenExpired': 'Istunto vanheni. Etsi varaus uudelleen jatkaaksesi.',
    'errors.registrationNotCompleted': 'Vierasrekisteröinti ei ole vielä valmis.',
    'errors.invalidDateRange': 'Lähtöpäivän on oltava saapumispäivän jälkeen.',
    'errors.temporaryServiceError': 'Palvelu on tilapäisesti poissa käytöstä. Yritä hetken kuluttua uudelleen.',
    'errors.claimMissingFields': 'Anna varauskanava, majoituksen päivämäärät ja sukunimi.',
    'errors.registrationMissingFields': 'Täytä kaikki pakolliset vierastiedot ennen lähetystä.',
    'errors.invalidEmail': 'Anna kelvollinen sähköpostiosoite.',
    'errors.invalidFinnishSocialId': 'Anna kelvollinen suomalainen henkilötunnus.',
    'errors.invalidGuestCount': 'Valitse 1–4 vierasta ja täytä vastaava määrä vierastietoja.',
    'errors.unknownChannelHost':
      'Avaa sinulle lähetetty Airbnb- tai Booking.com-sisäänkirjautumislinkki ja yritä uudelleen.',
  },
} as const;

export type CopyKey = keyof (typeof copy)['en'];